import React, { useEffect } from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Fuel, PhoneCall, MapPin, ShieldCheck, Clock, Truck, FileText, Star, ArrowLeft, CheckCircle2 } from 'lucide-react';

const WA_LINK = 'https://wa.me/917042722262?text=' + encodeURIComponent('Hi Mobile Fuel Kart! I need doorstep diesel delivery.');

const CITIES = {
  noida: {
    name: 'Noida',
    h1: 'Doorstep Diesel Delivery in Noida',
    intro: 'Mobile Fuel Kart brings BPCL-quality diesel straight to your site anywhere in Noida. Our PESO-licensed mobile fuel karts refuel DG sets, construction machinery, industrial boilers and fleet vehicles — with transparent per-litre pricing, live tracking and an instant GST tax invoice for the exact delivered quantity.',
    localities: ['Sector 62', 'Sector 63', 'Sector 80', 'Sector 95', 'NSEZ', 'Sector 132', 'Sector 135', 'Noida-Greater Noida Expressway', 'Phase 2 Industrial Area', 'Film City'],
    useCases: ['DG set & generator refuelling for offices, IT parks and hospitals', 'Bulk diesel for construction sites and tower cranes', 'Industrial diesel supply for factories in Phase 2 & NSEZ', 'Fleet and logistics depot refuelling', 'Housing society and mall genset top-ups'],
    faqs: [
      { q: 'Do you deliver diesel to all sectors of Noida?', a: 'Yes — we cover every sector of Noida including Sector 62, 63, 80, 95, 132, 135, NSEZ and the Noida-Greater Noida Expressway corridor. Schedule your slot and our fuel kart arrives at your gate.' },
      { q: 'What is the minimum diesel order quantity in Noida?', a: 'We serve business and bulk requirements — from generator top-ups to multi-thousand-litre construction site refuelling. Call +91 70427 22262 for current minimums and pricing.' },
      { q: 'How fast can I get diesel delivered in Noida?', a: 'Choose a scheduled delivery slot that suits you — including same-day slots subject to availability. Our team confirms every order and you can track the tanker live.' },
    ],
  },
  'greater-noida': {
    name: 'Greater Noida',
    h1: 'Doorstep Diesel Delivery in Greater Noida',
    intro: 'On-demand diesel delivery across Greater Noida and Greater Noida West (Noida Extension). Mobile Fuel Kart\u2019s PESO-approved bowsers refuel RMC plants, construction projects, industrial units and residential society gensets — quality-checked at OMC outlets before every dispatch.',
    localities: ['Knowledge Park', 'Ecotech I / II / III', 'Surajpur', 'Kasna', 'Pari Chowk', 'Greater Noida West (Noida Extension)', 'Yamuna Expressway', 'Jewar Corridor', 'Site C & Site V Industrial Areas'],
    useCases: ['Construction equipment and earthmover refuelling along Yamuna Expressway', 'RMC and batching plant diesel supply', 'Industrial units in Ecotech & Site C', 'DG sets for high-rise societies in Greater Noida West', 'Warehouse and logistics park refuelling'],
    faqs: [
      { q: 'Do you deliver diesel in Greater Noida West (Noida Extension)?', a: 'Yes — Greater Noida West, including all major society and commercial belts, is fully covered along with Knowledge Park, Ecotech zones, Surajpur and the Yamuna Expressway corridor.' },
      { q: 'Can builders schedule recurring diesel deliveries in Greater Noida?', a: 'Yes. Construction companies can schedule slot-based deliveries for machinery and cranes, with consolidated monthly statements and GST tax invoices for every drop.' },
      { q: 'Is your diesel quality certified?', a: 'Every load is quality and quantity tested at OMC (Oil Marketing Company) outlets before delivery, and dispensed via calibrated, PESO-approved metering.' },
    ],
  },
  ghaziabad: {
    name: 'Ghaziabad',
    h1: 'Doorstep Diesel Delivery in Ghaziabad',
    intro: 'Trusted bulk diesel supplier for Ghaziabad\u2019s industries and businesses. Mobile Fuel Kart delivers to Sahibabad Industrial Area, Raj Nagar Extension, Indirapuram and the NH-9 corridor — 24/7 emergency response for factories, cold storage, hospitals and fleet depots.',
    localities: ['Sahibabad Industrial Area', 'Raj Nagar Extension', 'Indirapuram', 'Vaishali', 'Kaushambi', 'NH-9 Corridor', 'Loni Industrial Area', 'Meerut Road Industrial Area', 'Masuri'],
    useCases: ['Factory and industrial diesel supply in Sahibabad', '24/7 emergency diesel for cold storage and hospitals', 'Tower crane and construction refuelling in Raj Nagar Extension', 'Fleet depots and transporters along NH-9', 'Genset refuelling for malls and societies in Indirapuram'],
    faqs: [
      { q: 'Do you provide emergency diesel delivery in Ghaziabad?', a: 'Yes — we operate a 24/7 emergency response for critical facilities like hospitals, cold storage and factories across Ghaziabad. Call +91 70427 22262 any time.' },
      { q: 'Which industrial areas of Ghaziabad do you cover?', a: 'Sahibabad Industrial Area, Loni, Meerut Road Industrial Area, Masuri and units along the NH-9 corridor, plus commercial hubs like Indirapuram, Vaishali and Kaushambi.' },
      { q: 'Do I get a GST invoice for bulk diesel in Ghaziabad?', a: 'Yes. Every delivery generates a digital TAX INVOICE for the exact delivered quantity, with consolidated monthly statements available for regular customers.' },
    ],
  },
};

const TRUST = [
  { icon: ShieldCheck, label: 'BPCL Authorized Dealer' },
  { icon: CheckCircle2, label: 'PESO-Licensed Fuel Karts' },
  { icon: Clock, label: '24/7 Emergency Response' },
  { icon: FileText, label: 'GST Tax Invoice on Every Drop' },
];

export default function CityLanding() {
  const { city } = useParams();
  const nav = useNavigate();
  const data = CITIES[city];

  useEffect(() => {
    if (!data) return;
    const prevTitle = document.title;
    document.title = `Diesel Delivery in ${data.name} | Doorstep Diesel at Your Site — Mobile Fuel Kart`;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute('content');
    meta?.setAttribute('content', `${data.h1} — BPCL authorized, PESO-licensed. Bulk diesel for DG sets, construction & industry. Order online or call +91 70427 22262.`);
    window.scrollTo(0, 0);
    return () => { document.title = prevTitle; if (prevDesc) meta?.setAttribute('content', prevDesc); };
  }, [data]);

  if (!data) return <Navigate to="/login" replace />;
  const others = Object.entries(CITIES).filter(([slug]) => slug !== city);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={`city-landing-${city}`}>
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 h-14 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2 font-semibold" data-testid="city-header-home-link">
            <ArrowLeft className="h-4 w-4" /><Fuel className="h-5 w-5 text-primary" />Mobile Fuel Kart
          </Link>
          <div className="flex items-center gap-2">
            <a href="tel:+917042722262"><Button size="sm" variant="secondary" data-testid="city-call-button"><PhoneCall className="h-4 w-4 mr-1.5" />+91 70427 22262</Button></a>
            <Button size="sm" className="bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => nav('/signin')} data-testid="city-order-button"><Fuel className="h-4 w-4 mr-1.5" />Order Fuel Now</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden lux-noise">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true"
          style={{ background: 'radial-gradient(1000px 500px at 20% 10%, hsl(var(--primary) / 0.16), transparent 60%)' }} />
        <div className="relative mx-auto max-w-4xl px-4 py-14 text-left">
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full lux-glass px-3 py-1 text-xs font-semibold"><MapPin className="h-3.5 w-3.5 text-primary" />Serving {data.name}</span>
            <span className="inline-flex items-center gap-1.5 rounded-full lux-glass px-3 py-1 text-xs font-semibold"><Star className="h-3.5 w-3.5 text-accent fill-accent" />4.9/5 · 300+ ratings</span>
          </div>
          <h1 className="mt-5 font-luxe font-semibold tracking-tight text-4xl sm:text-5xl leading-[1.06]" data-testid="city-h1">{data.h1}</h1>
          <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-2xl" data-testid="city-intro">{data.intro}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button size="lg" className="min-h-12 px-6 font-semibold bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => nav('/signin')} data-testid="city-hero-order-button"><Fuel className="h-5 w-5 mr-2" />Order Diesel in {data.name}</Button>
            <a href={WA_LINK} target="_blank" rel="noreferrer"><Button size="lg" variant="secondary" className="min-h-12 border border-border" data-testid="city-hero-whatsapp-button">WhatsApp Us</Button></a>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
            {TRUST.map((t) => (
              <div key={t.label} className="rounded-lg border border-border bg-card px-3 py-2.5 flex items-center gap-2 text-xs font-medium">
                <t.icon className="h-4 w-4 text-primary shrink-0" />{t.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Localities */}
      <section className="py-12 bg-muted/40 border-y border-border">
        <div className="mx-auto max-w-4xl px-4">
          <h2 className="font-luxe text-2xl font-semibold">Areas We Serve in {data.name}</h2>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="city-localities">
            {data.localities.map((l) => (
              <span key={l} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm"><MapPin className="h-3.5 w-3.5 text-primary" />{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="py-12">
        <div className="mx-auto max-w-4xl px-4">
          <h2 className="font-luxe text-2xl font-semibold">What We Fuel in {data.name}</h2>
          <ul className="mt-4 space-y-2.5" data-testid="city-usecases">
            {data.useCases.map((u) => (
              <li key={u} className="flex items-start gap-2.5 text-sm text-muted-foreground"><Truck className="h-4 w-4 text-primary shrink-0 mt-0.5" />{u}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* City FAQs */}
      <section className="py-12 bg-muted/40 border-y border-border">
        <div className="mx-auto max-w-4xl px-4">
          <h2 className="font-luxe text-2xl font-semibold">Diesel Delivery in {data.name} — FAQs</h2>
          <Accordion type="single" collapsible className="mt-4">
            {data.faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`cf-${i}`} data-testid={`city-faq-${i}`}>
                <AccordionTrigger className="text-left text-sm font-medium">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA + interlinks */}
      <section className="py-14">
        <div className="mx-auto max-w-4xl px-4 text-left">
          <div className="rounded-2xl border border-border bg-card p-8">
            <h2 className="font-luxe text-2xl font-semibold">Need diesel delivered in {data.name} today?</h2>
            <p className="mt-2 text-sm text-muted-foreground">Order online in minutes or call our hotline — our team will schedule your delivery slot right away.</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => nav('/signin')} data-testid="city-cta-order-button"><Fuel className="h-5 w-5 mr-2" />Order Fuel Now</Button>
              <a href="tel:+917042722262"><Button size="lg" variant="secondary" className="border border-border"><PhoneCall className="h-4 w-4 mr-2" />Call +91 70427 22262</Button></a>
            </div>
          </div>
          <p className="mt-8 text-sm text-muted-foreground">
            Also serving:{' '}
            {others.map(([slug, c], i) => (
              <span key={slug}>
                <Link to={`/diesel-delivery/${slug}`} className="text-primary font-medium hover:underline" data-testid={`city-link-${slug}`}>Diesel Delivery in {c.name}</Link>
                {i < others.length - 1 ? ' · ' : ''}
              </span>
            ))}
            {' '}· <Link to="/login" className="text-primary font-medium hover:underline">Mobile Fuel Kart Home</Link>
          </p>
        </div>
      </section>

      <footer className="py-6 border-t border-border text-center text-xs text-muted-foreground">
        Mobile Fuel Kart · PP1, Mahamaya Flyover, Sector 95, Noida - 201301 · BPCL Authorized · PESO Licensed
      </footer>
    </div>
  );
}
